/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Container,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Typography,
    Chip,
    Link,
    Collapse,
    IconButton,
    Tooltip,
    Tabs,
    Tab,
    useTheme,
    alpha,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import { useNavigate } from 'react-router-dom';
import axios from '../config/axiosConfig';
import TitleAndInfo from './TitleAndInfo';
import debounce from 'lodash/debounce';
import NewsSentiment from './NewsSentiment';

// ─── Constants ───────────────────────────────────────────────────────
const VIEW_STORAGE_KEY = 'news-view-preference';
const TAB_STORAGE_KEY = 'news-tab-preference';
const VIEW_FEED = 'feed';
const VIEW_GRID = 'grid';

const TAB_CONFIG = [
    {
        key: 'stock',
        label: 'Stock',
        icon: <ShowChartIcon />,
        endpoint: '/api/news/paginated',
        hasSymbol: true,
        hasSentiment: true,
    },
    {
        key: 'crypto',
        label: 'Crypto',
        icon: <CurrencyBitcoinIcon />,
        endpoint: '/api/news/crypto/paginated',
        hasSymbol: true,
        hasSentiment: false,
    },
    {
        key: 'general',
        label: 'General',
        icon: <NewspaperIcon />,
        endpoint: '/api/news/general/paginated',
        hasSymbol: false,
        hasSentiment: false,
    },
];

// ─── Sentiment helpers ───────────────────────────────────────────────
const sentimentConfig = {
    Positive: { color: '#16a34a', bg: '#dcfce7', label: 'Positive' },
    Negative: { color: '#dc2626', bg: '#fee2e2', label: 'Negative' },
    Neutral: { color: '#6b7280', bg: '#f3f4f6', label: 'Neutral' },
};

const getSentimentStyle = (sentiment) =>
    sentimentConfig[sentiment] || sentimentConfig.Neutral;

// ─── Relative time formatter ─────────────────────────────────────────
const timeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ─── View Toggle Button ──────────────────────────────────────────────
const ViewToggle = ({ viewMode, onToggle }) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                display: 'inline-flex',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                overflow: 'hidden',
            }}
        >
            <Tooltip title="Feed view" arrow>
                <IconButton
                    size="small"
                    onClick={() => onToggle(VIEW_FEED)}
                    sx={{
                        borderRadius: 0,
                        px: 1.2,
                        py: 0.8,
                        bgcolor: viewMode === VIEW_FEED
                            ? alpha(theme.palette.primary.main, 0.12)
                            : 'transparent',
                        color: viewMode === VIEW_FEED
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                        '&:hover': {
                            bgcolor: viewMode === VIEW_FEED
                                ? alpha(theme.palette.primary.main, 0.18)
                                : alpha(theme.palette.action.hover, 0.08),
                        },
                    }}
                >
                    <ViewListIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Tooltip>
            <Tooltip title="Grid view" arrow>
                <IconButton
                    size="small"
                    onClick={() => onToggle(VIEW_GRID)}
                    sx={{
                        borderRadius: 0,
                        px: 1.2,
                        py: 0.8,
                        borderLeft: `1px solid ${theme.palette.divider}`,
                        bgcolor: viewMode === VIEW_GRID
                            ? alpha(theme.palette.primary.main, 0.12)
                            : 'transparent',
                        color: viewMode === VIEW_GRID
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                        '&:hover': {
                            bgcolor: viewMode === VIEW_GRID
                                ? alpha(theme.palette.primary.main, 0.18)
                                : alpha(theme.palette.action.hover, 0.08),
                        },
                    }}
                >
                    <ViewModuleIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

// ─── Feed Item (compact list row) ────────────────────────────────────
const FeedItem = ({ article, tabConfig }) => {
    const [expanded, setExpanded] = useState(false);
    const theme = useTheme();
    const navigate = useNavigate();
    const sentiment = getSentimentStyle(article.sentiment);
    const hasSentiment = tabConfig.hasSentiment && article.sentiment;

    const handleSymbolClick = (e) => {
        e.stopPropagation();
        if (article.symbol) {
            navigate(`/asset/${article.symbol}`);
        }
    };

    return (
        <Box
            onClick={() => setExpanded((prev) => !prev)}
            sx={{
                display: 'flex',
                borderLeft: hasSentiment ? `3px solid ${sentiment.color}` : `3px solid ${theme.palette.divider}`,
                py: 1.5,
                px: 2,
                cursor: 'pointer',
                transition: 'background 0.15s ease',
                '&:hover': {
                    bgcolor: alpha(theme.palette.action.hover, 0.04),
                },
                '&:not(:last-child)': {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                },
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Meta row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                    {article.symbol && (
                        <Chip
                            label={article.symbol}
                            size="small"
                            clickable
                            onClick={handleSymbolClick}
                            sx={{
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                height: 22,
                                borderRadius: '4px',
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                color: theme.palette.primary.main,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    bgcolor: theme.palette.primary.main,
                                    color: 'white',
                                    transform: 'scale(1.05)',
                                },
                            }}
                        />
                    )}
                    {hasSentiment && (
                        <Chip
                            label={sentiment.label}
                            size="small"
                            sx={{
                                fontWeight: 600,
                                fontSize: '0.65rem',
                                height: 20,
                                borderRadius: '4px',
                                bgcolor: sentiment.bg,
                                color: sentiment.color,
                            }}
                        />
                    )}
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.3,
                            fontSize: '0.72rem',
                        }}
                    >
                        <AccessTimeIcon sx={{ fontSize: 13 }} />
                        {timeAgo(article.publishedDate)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                        {article.publisher || article.site}
                    </Typography>
                </Box>

                {/* Title */}
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        lineHeight: 1.4,
                        color: 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: expanded ? 'unset' : 2,
                        WebkitBoxOrient: 'vertical',
                    }}
                >
                    {article.title}
                </Typography>

                {/* Expandable snippet */}
                <Collapse in={expanded} timeout={200}>
                    <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', mt: 0.75, lineHeight: 1.55, fontSize: '0.82rem' }}
                    >
                        {article.text}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Link
                            href={article.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                                fontSize: '0.78rem',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.4,
                            }}
                        >
                            Read full article <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Link>
                        {hasSentiment && article.sentimentScore !== undefined && (
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                Score: {article.sentimentScore.toFixed(2)}
                            </Typography>
                        )}
                    </Box>
                </Collapse>
            </Box>

            {/* Expand chevron */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', pl: 1, pt: 0.25 }}>
                <IconButton
                    size="small"
                    sx={{
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        p: 0.25,
                    }}
                >
                    <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                </IconButton>
            </Box>
        </Box>
    );
};

// ─── Main News component ─────────────────────────────────────────────
const News = () => {
    const [newsArticles, setNewsArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [symbolFilter, setSymbolFilter] = useState('');
    const [sentimentFilter, setSentimentFilter] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [activeTab, setActiveTab] = useState(() => {
        try {
            const saved = localStorage.getItem(TAB_STORAGE_KEY);
            return saved !== null ? parseInt(saved) : 0;
        } catch {
            return 0;
        }
    });
    const [viewMode, setViewMode] = useState(() => {
        try {
            return localStorage.getItem(VIEW_STORAGE_KEY) || VIEW_FEED;
        } catch {
            return VIEW_FEED;
        }
    });
    const observerTarget = useRef(null);
    const currentFiltersRef = useRef({ search: '', symbol: '', sentiment: '' });
    const theme = useTheme();

    const currentTabConfig = TAB_CONFIG[activeTab];

    const titleTop = 'News Feed';
    const title = 'Market & General News';
    const info =
        'Stay updated with the latest stock market, crypto, and general news articles.';

    // ── Tab change handler ───────────────────────────────────────────
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        try {
            localStorage.setItem(TAB_STORAGE_KEY, newValue);
        } catch {
            // localStorage not available
        }
        // Reset state
        setNewsArticles([]);
        setSearchTerm('');
        setSymbolFilter('');
        setSentimentFilter('');
        setPage(1);
        setHasMore(true);
        setError(null);
        currentFiltersRef.current = { search: '', symbol: '', sentiment: '' };
    };

    // ── View toggle handler ──────────────────────────────────────────
    const handleViewToggle = (mode) => {
        setViewMode(mode);
        try {
            localStorage.setItem(VIEW_STORAGE_KEY, mode);
        } catch {
            // localStorage not available
        }
    };

    // ── Data fetching ────────────────────────────────────────────────
    const fetchNews = useCallback(
        async (isNewSearch = false, filters = {}) => {
            if (!hasMore && !isNewSearch) return;

            try {
                setLoading(true);
                const currentPage = isNewSearch ? 1 : page;
                const searchQuery = isNewSearch ? filters : currentFiltersRef.current;
                const tabConfig = TAB_CONFIG[activeTab];

                const params = {
                    page: currentPage,
                    limit: 20,
                    search: searchQuery.search || '',
                };

                if (tabConfig.hasSymbol) {
                    params.symbol = searchQuery.symbol || '';
                }

                if (tabConfig.hasSentiment) {
                    params.sentiment = searchQuery.sentiment || '';
                }

                const response = await axios.get(tabConfig.endpoint, { params });

                const { data: responseData, pagination } = response.data;

                if (isNewSearch) {
                    setNewsArticles(responseData);
                    setPage(1);
                    currentFiltersRef.current = searchQuery;
                } else {
                    setNewsArticles((prev) => [...prev, ...responseData]);
                }

                setHasMore(pagination?.hasMore || responseData.length === 20);
                setError(null);
            } catch (err) {
                console.error('Error fetching news:', err);
                setError(err.message || 'Failed to fetch news data');
            } finally {
                setLoading(false);
            }
        },
        [page, hasMore, activeTab],
    );

    // ── Debounced search ─────────────────────────────────────────────
    const debouncedSearch = useCallback(
        debounce((filters) => {
            fetchNews(true, filters);
        }, 800),
        [fetchNews],
    );

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        debouncedSearch.cancel();
        debouncedSearch({ search: val.trim(), symbol: symbolFilter, sentiment: sentimentFilter });
    };

    const handleSymbolFilterChange = (e) => {
        const val = e.target.value;
        setSymbolFilter(val);
        debouncedSearch.cancel();
        debouncedSearch({ search: searchTerm.trim(), symbol: val, sentiment: sentimentFilter });
    };

    const handleSentimentFilterChange = (e) => {
        const val = e.target.value;
        setSentimentFilter(val);
        debouncedSearch.cancel();
        debouncedSearch({ search: searchTerm.trim(), symbol: symbolFilter, sentiment: val });
    };

    // ── Infinite scroll observer ─────────────────────────────────────
    const handleObserver = useCallback(
        (entries) => {
            const target = entries[0];
            if (target.isIntersecting && hasMore && !loading) {
                setPage((prev) => prev + 1);
            }
        },
        [hasMore, loading],
    );

    useEffect(() => {
        const observer = new IntersectionObserver(handleObserver, {
            root: null,
            rootMargin: '20px',
            threshold: 0.1,
        });
        if (observerTarget.current) observer.observe(observerTarget.current);
        return () => {
            if (observerTarget.current) observer.unobserve(observerTarget.current);
        };
    }, [handleObserver]);

    // Initial load & tab change
    useEffect(() => {
        fetchNews(true, { search: '', symbol: '', sentiment: '' });
    }, [activeTab]);

    // Pagination
    useEffect(() => {
        if (page > 1) fetchNews(false);
    }, [page, fetchNews]);

    // ── Error state ──────────────────────────────────────────────────
    if (error) {
        return (
            <Container maxWidth="xl">
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    // ── Render ───────────────────────────────────────────────────────
    return (
        <Container maxWidth="xl">
            <TitleAndInfo titleTop={titleTop} title={title} info={info} />

            {/* ── Tabs ────────────────────────────────────────────── */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="standard"
                >
                    {TAB_CONFIG.map((tab) => (
                        <Tab
                            key={tab.key}
                            icon={tab.icon}
                            label={tab.label}
                            iconPosition="start"
                            sx={{ minHeight: 48, textTransform: 'none', fontWeight: 600 }}
                        />
                    ))}
                </Tabs>
            </Box>

            {/* ── Filters + View Toggle ───────────────────────────── */}
            <Grid container spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder="Search news articles..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                {currentTabConfig.hasSymbol && (
                    <Grid item xs={12} sm={3} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            placeholder="Symbol (e.g., AAPL)"
                            value={symbolFilter}
                            onChange={handleSymbolFilterChange}
                            inputProps={{ style: { textTransform: 'uppercase' } }}
                        />
                    </Grid>
                )}
                {currentTabConfig.hasSentiment && (
                    <Grid item xs={12} sm={3} md={2}>
                        <FormControl fullWidth size="small" variant="outlined">
                            <InputLabel>Sentiment</InputLabel>
                            <Select
                                value={sentimentFilter}
                                onChange={handleSentimentFilterChange}
                                label="Sentiment"
                            >
                                <MenuItem value="">All Sentiment</MenuItem>
                                <MenuItem value="positive">Positive</MenuItem>
                                <MenuItem value="negative">Negative</MenuItem>
                                <MenuItem value="neutral">Neutral</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                )}
                <Grid item xs={12} md={currentTabConfig.hasSymbol && currentTabConfig.hasSentiment ? 4 : currentTabConfig.hasSymbol ? 6 : 8} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                    <ViewToggle viewMode={viewMode} onToggle={handleViewToggle} />
                </Grid>
            </Grid>

            {/* ── Initial loader ──────────────────────────────────── */}
            {loading && newsArticles.length === 0 && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            )}

            {/* ── Feed view ───────────────────────────────────────── */}
            {newsArticles.length > 0 && viewMode === VIEW_FEED && (
                <Box
                    sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        overflow: 'hidden',
                        bgcolor: 'background.paper',
                    }}
                >
                    {newsArticles.map((article) => (
                        <FeedItem key={article._id} article={article} tabConfig={currentTabConfig} />
                    ))}
                </Box>
            )}

            {/* ── Grid view (NewsSentiment) ───────────────────────── */}
            {newsArticles.length > 0 && viewMode === VIEW_GRID && (
                <NewsSentiment data={newsArticles} loading={false} />
            )}

            {/* ── Empty state ─────────────────────────────────────── */}
            {!loading && newsArticles.length === 0 && (
                <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                    <Alert severity="info">No news articles found. Try adjusting your search criteria.</Alert>
                </Box>
            )}

            {/* ── Scroll sentinel & bottom loader ─────────────────── */}
            <Box
                ref={observerTarget}
                sx={{ display: 'flex', justifyContent: 'center', p: 2, minHeight: '80px' }}
            >
                {loading && newsArticles.length > 0 && <CircularProgress size={28} />}
                {!hasMore && newsArticles.length > 0 && (
                    <Alert severity="info" sx={{ width: 'auto' }}>
                        You've reached the end of the news feed.
                    </Alert>
                )}
            </Box>
        </Container>
    );
};

export default News;